import React, { useState } from "react";
import { View, StyleSheet, Platform } from "react-native";
import {
  ArrowRight,
  ArrowsClockwise,
  Users,
  ClipboardText,
  CheckCircle,
  FileMagnifyingGlass,
} from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";

interface CourseEvaluationCardProps {
  title: string;
  courseCode?: string;
  courseDate?: string;
  studentCount: number;
  assignmentCount: number;
  lastSynced?: string;
  evaluatedDate?: string;
  onViewEvaluation: () => void;
  onEvaluate: () => void;
}

const CourseEvaluationCard: React.FC<CourseEvaluationCardProps> = ({
  title,
  courseCode,
  courseDate,
  studentCount,
  assignmentCount,
  lastSynced,
  evaluatedDate,
  onViewEvaluation,
  onEvaluate,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const handleMouseEnter = () => {
    if (Platform.OS === "web") {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (Platform.OS === "web") {
      setIsHovered(false);
    }
  };

  return (
    <View
      style={[styles.card, isHovered && styles.cardHovered]}
      // @ts-ignore - onMouseEnter/onMouseLeave works on web
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <CustomText
        useTranslationText={false}
        text={title}
        size="xl"
        weight="bold"
        style={styles.title}
        aria-level="3"
        role="heading"
      />
      <CustomSpacing type="vertical" size="xs" />

      <View style={styles.courseInfoRow}>
        {courseCode && courseCode.trim() && (
          <CustomText useTranslationText={false} text={courseCode} size="s" style={styles.courseCodeText} />
        )}
        {courseCode && courseCode.trim() && courseDate && (
          <CustomText useTranslationText={false} text="•" size="s" style={styles.courseInfoSeparator} />
        )}
        {courseDate && (
          <CustomText useTranslationText={false} text={courseDate} size="s" style={styles.courseCodeText} />
        )}
      </View>

      <View style={styles.statsRow}>
        <View style={styles.statItem}>
          <Users size={20} color="#98a2b3" weight="regular" />
          <CustomText
            useTranslationText={false}
            text={`${studentCount} student${studentCount !== 1 ? "s" : ""}`}
            size="s"
            style={styles.statText}
          />
        </View>
        {assignmentCount > 0 && (
          <>
            <View style={styles.statSpacer} />
            <View style={styles.statItem}>
              <ClipboardText size={20} color="#98a2b3" weight="regular" />
              <CustomText
                useTranslationText={false}
                text={`${assignmentCount} assignment${assignmentCount !== 1 ? "s" : ""}`}
                size="s"
                style={styles.statText}
              />
            </View>
          </>
        )}
      </View>

      {lastSynced && (
        <View style={styles.metaRow}>
          <ArrowsClockwise size={16} color="#667085" weight="regular" />
          <CustomText useTranslationText={false} text="Last synced:" size="xs" style={styles.metaLabel} />
          <CustomText useTranslationText={false} text={lastSynced} size="xs" style={styles.metaLabel} />
        </View>
      )}

      <View style={styles.buttonContainer}>
        {evaluatedDate ? (
          <>
            <View style={styles.evaluatedInfo}>
              <CheckCircle size={16} color="#12B76A" weight="regular" />
              <CustomText useTranslationText={false} text="Evaluated on" size="s" style={styles.metaLabel} />
              <CustomText useTranslationText={false} text={evaluatedDate} size="s" style={styles.metaLabel} />
            </View>
            <View
              // @ts-ignore - onMouseEnter/onMouseLeave works on web
              onMouseEnter={() => Platform.OS === "web" && setIsButtonHovered(true)}
              onMouseLeave={() => Platform.OS === "web" && setIsButtonHovered(false)}>
              <CustomButton
                text="View Evaluation"
                onPress={onViewEvaluation}
                styleType="light"
                size="s"
                rightIcon={<ArrowRight size={16} color="#344054" weight="regular" />}
                style={[styles.viewButton, isButtonHovered && styles.viewButtonHovered]}
                textStyle={styles.viewButtonText}
                useTranslationText={false}
              />
            </View>
          </>
        ) : (
          <CustomButton
            text="Evaluate Course"
            onPress={onEvaluate}
            styleType="primary"
            size="s"
            leftIcon={<FileMagnifyingGlass size={16} color="#FFFFFF" weight="regular" />}
            style={styles.evaluateButton}
            textStyle={styles.evaluateButtonText}
            useTranslationText={false}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 24,
    backgroundColor: "#fff9",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    ...(Platform.OS === "web"
      ? ({
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "default",
        } as any)
      : {}),
  },
  cardHovered: {
    ...(Platform.OS === "web"
      ? ({
          transform: [{ translateY: -4 }, { scale: 1.02 }],
          shadowColor: "rgba(0, 0, 0, 0.12)",
          shadowOffset: { width: 0, height: 12 },
          shadowOpacity: 1,
          shadowRadius: 32,
          elevation: 8,
          borderColor: "#D0D5DD",
        } as any)
      : {}),
  },
  title: {
    color: "#101828",
  },
  courseInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    minHeight: 20,
  },
  courseCodeText: {
    color: "#475467",
  },
  courseInfoSeparator: {
    color: "#475467",
    marginHorizontal: 8,
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  statItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statSpacer: {
    width: 16,
  },
  statText: {
    color: "#475467",
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  metaLabel: {
    color: "#475467",
  },
  buttonContainer: {
    marginTop: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  evaluatedInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  evaluateButton: {
    width: "100%",
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  evaluateButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
  },
  viewButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    shadowColor: "rgba(16, 24, 40, 0.1)",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 2,
    elevation: 1,
    ...Platform.select({
      web: {
        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
      },
    }),
  },
  viewButtonHovered: {
    ...(Platform.OS === "web"
      ? ({
          backgroundColor: "#F9FAFB",
          transform: [{ scale: 1.02 }],
          shadowColor: "rgba(16, 24, 40, 0.15)",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 1,
          shadowRadius: 4,
          elevation: 2,
        } as any)
      : {}),
  },
  viewButtonText: {
    color: "#344054",
    fontSize: 12,
    fontWeight: "500",
  },
});

export default CourseEvaluationCard;
